/** @jsxRuntime classic */
/** @jsx jsx */
// eslint-disable-next-line
import { jsx } from '@emotion/react';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApiGroupRequest } from '@ibe/api';
import { observer } from 'mobx-react';
import React, { useState, JSX } from 'react';
import { Button, Col, Modal, ModalBody, Row } from 'reactstrap';
import fallback from '../../../_Translations/generated/group-request.en.t.json';
import Keys from '../../../_Translations/generated/group-request.en.t.json.keys';
import useAppService from '../../Hooks/useAppService';
import useAuth from '../../Hooks/useAuth';
import useTranslation from '../../Hooks/useTranslation';
import Overlay from '../UI/LoadingOverlay/Overlay';
import OverlayContainer from '../UI/LoadingOverlay/OverlayContainer';
import Error from '../UI/Messages/Error';
import Info from '../UI/Messages/Info';
import GroupRequest from './GroupRequest';

export interface Props {
  isOpen?: boolean;
  setIsOpen?: (newValue: boolean) => void;
  withNoAuthorization?: boolean;
  id?: string;
}

const GroupRequestModule = observer(
  (props: Props): JSX.Element => {
    const { isOpen, setIsOpen, withNoAuthorization, id = 'groupRequest_dropdown' } = props;
    const { t } = useTranslation('group-request', fallback, Keys);
    const { authState } = useAuth();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [userNotLoggedInError, setUserNotLoggedInError] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);

    function openForm(): void {
      setError(false);
      setSuccess(false);
      if (withNoAuthorization) {
        if (setIsOpen) {
          setIsOpen(true);
        }
      } else if (authState.isLoggedIn) {
        setUserNotLoggedInError(false);
        if (setIsOpen) {
          setIsOpen(true);
        }
      } else {
        setUserNotLoggedInError(true);
      }
    }

    const apiService = useAppService();

    function onSendAction(groupRequest: ApiGroupRequest): void {
      setLoading(true);
      setError(false);
      setSuccess(false);

      apiService.api
        .sendGroupRequest(groupRequest)
        .then(() => {
          setSuccess(true);
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }

    function onCancelAction(): void {
      if (setIsOpen) {
        setIsOpen(false);
      }
    }

    const Content = () => {
      return (
        <div>
          <OverlayContainer>
            <div>
              <GroupRequest onSendAction={onSendAction} onCancelAction={onCancelAction} id={id} />
              <div className="pt-2">
                {success && <Info message={t(Keys.messages.success)} />}
                {error && <Error message={t(Keys.messages.error)} />}
              </div>
            </div>
            {loading && <Overlay />}
          </OverlayContainer>
        </div>
      );
    };

    function toggleModal(): void {
      if (setIsOpen) {
        setIsOpen(!isOpen);
      }
    }

    return (
      <div key="group_request_module">
        <div className="my-1">
          <Col>
            <Row>
              <div className="font-weight-bold">{t(Keys.groupRequestModuleLabel)}</div>
            </Row>
            <Row>
              <Button color="link p-0" onClick={(): void => openForm()}>
                {t(Keys.button.clickHere)}
                <FontAwesomeIcon icon={faChevronRight} className="fa-sm ml-1" />
              </Button>
            </Row>
          </Col>
        </div>
        <Modal
          isOpen={isOpen}
          toggle={toggleModal}
          backdrop="static"
          keyboard={false}
          className="iso__modal-dialog"
          size="lg"
        >
          <ModalBody>
            <Content />
          </ModalBody>
        </Modal>
        {userNotLoggedInError && <Error message={t(Keys.messages.userNotLoggedInError)} />}
      </div>
    ) as JSX.Element;
  }
);

export default GroupRequestModule;
